import React from 'react';
import DOMPurify from 'dompurify';
import { Button } from '../../components/ui/button';
import { Card } from '../../components/ui/card';
import { TabsContent } from '../../components/ui/tabs';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Send, Filter, Trash2, Edit, Search } from 'lucide-react';
import RichTextEditor from '../../components/RichTextEditor';
import FilePickerInput from '../../components/FilePickerInput';
import YouTubeEmbed from '../../components/YouTubeEmbed';
import LessonFiles from '../../components/LessonFiles';
import MessageFiles from '../../components/MessageFiles';

const getFullName = (user) => {
  if (!user) return '';
  const full = `${user.first_name || ''} ${user.last_name || ''}`.trim();
  return full || user.username || '';
};

export default function LessonsTab({ ctx }) {
  const {
    user,
    token,
    classes,
    selectedClass,
    setSelectedClass,
    lessons,
    filteredLessons,
    lessonDate,
    setLessonDate,
    lessonDescription,
    setLessonDescription,
    lessonFiles,
    setLessonFiles,
    lessonFilter,
    setLessonFilter,
    handleCreateLesson,
    handleEditLesson,
    handleDeleteLesson,
    boardClassId,
    setBoardClassId,
    boardContent,
    setBoardContent,
    boardFiles,
    setBoardFiles,
    boardFilter,
    setBoardFilter,
    handleSendBoardMessage,
    boardMessages,
    filteredBoardMessages,
    privateRecipient,
    setPrivateRecipient,
    privateContent,
    setPrivateContent,
    privateFiles,
    setPrivateFiles,
    privateFilter,
    setPrivateFilter,
    handleSendPrivateMessage,
    privateMessages,
    filteredPrivateMessages,
    recipients,
    messageSearch,
    setMessageSearch,
    getSenderRoleColor,
    handleEditMessage,
    handleDeleteMessage,
  } = ctx;
  return (
          <TabsContent value="lessons" data-testid="lessons-content">
            <div className="space-y-6">
              <div>
                <h2 className="text-xl font-semibold text-slate-900 mb-4">Запис на учебен час</h2>
                <Card className="p-6">
                  <form onSubmit={handleCreateLesson} className="space-y-4">
                    <div>
                      <Label htmlFor="class">Клас</Label>
                      <Select value={selectedClass} onValueChange={setSelectedClass}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {classes.map((cls) => (
                            <SelectItem key={cls.class_id} value={cls.class_id}>
                              {cls.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <Label htmlFor="date">Дата</Label>
                      <Input
                        id="date"
                        data-testid="lesson-date-input"
                        type="date"
                        value={lessonDate}
                        onChange={(e) => setLessonDate(e.target.value)}
                        required
                        className="mt-1"
                      />
                    </div>

                    <div>
                      <Label>Описание на урока</Label>
                      <div className="mt-1">
                        <RichTextEditor
                          value={lessonDescription}
                          onChange={setLessonDescription}
                          placeholder="Опишете съдържанието на урока..."
                        />
                      </div>
                    </div>

                    <div>
                      <Label className="mb-2 block">Прикачени файлове (опционално)</Label>
                      <FilePickerInput
                        files={lessonFiles}
                        onChange={setLessonFiles}
                        label="Добавете файлове"
                      />
                    </div>

                    <Button
                      type="submit"
                      data-testid="create-lesson-btn"
                      className="w-full bg-[#00966E] hover:bg-[#007a59] text-white flex items-center justify-center gap-2"
                    >
                      <Send className="h-4 w-4" />
                      Създаване на запис
                    </Button>
                  </form>
                </Card>
              </div>

              <div>
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-xl font-semibold text-slate-900">Създадени записи</h2>
                  <Select value={lessonFilter} onValueChange={setLessonFilter}>
                    <SelectTrigger className="w-[140px] sm:w-[180px]">
                      <Filter className="h-4 w-4 mr-2" />
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Всички</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {filteredLessons.length === 0 ? (
                  <Card className="p-8 text-center text-slate-500">
                    Няма учебни записи за този клас
                  </Card>
                ) : (
                  <div className="space-y-3">
                    {filteredLessons.map((lesson) => (
                      <Card key={lesson.lesson_id} data-testid="lesson-card" className="p-4 border border-slate-200">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-3 mb-2">
                              <Badge variant="outline" className="text-xs">
                                {new Date(lesson.date).toLocaleDateString('bg-BG')}
                              </Badge>
                              {(lesson.file_paths?.length > 0 || lesson.file_path) && (
                                <Badge className="text-xs bg-green-100 text-green-700">
                                  С файл
                                </Badge>
                              )}
                            </div>
                            <p className="text-slate-900 prose prose-sm max-w-none" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(lesson.description) }} />
                            <YouTubeEmbed html={lesson.description} />
                            <LessonFiles lesson={lesson} token={token} />
                          </div>
                          <div className="flex items-center gap-1 ml-3 flex-shrink-0">
                            <Button
                              onClick={() => handleEditLesson(lesson)}
                              variant="ghost"
                              size="sm"
                              data-testid="edit-lesson-btn"
                              className="text-[#00966E] hover:text-[#007a59] hover:bg-green-50 h-8 w-8 p-0"
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button
                              onClick={() => handleDeleteLesson(lesson.lesson_id)}
                              variant="ghost"
                              size="sm"
                              data-testid="delete-lesson-btn"
                              className="text-[#D01C1F] hover:text-[#a81619] hover:bg-red-50 h-8 w-8 p-0"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      </Card>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </TabsContent>
  );
}
